/**
 * Per-user, per-grid column layout (order + width) persistence.
 *
 * Replicates the legacy ASP "Config" button behavior (see
 * PoliceCentralV2\ContainerCommon.vbs, Sub ConfigListView /
 * SaveConfigListView), which lets a user drag-reorder and drag-resize a
 * grid's columns and persists that layout scoped to the logged-in user and
 * that specific grid.
 *
 * Backed by the real `clientparams2` API (the legacy ClientParams2 table —
 * per-staffID scoped, distinct from the group-scoped `clientParams`/
 * `config.clientParams` API already used elsewhere in this app):
 *   GET  clientparams2/getBy/contextName/{moduleName} — all rows for a module
 *   POST clientparams2/save — upsert one row (clientParamID: 0 = create new)
 *
 * `paramValue` on the wire is the exact legacy serialization —
 * "<originalColumnIndex>:<newPosition>:<width>|..." (1-based, pipe-separated)
 * — so existing rows already saved by the legacy app (and any other legacy-
 * adjacent writer) are read/written compatibly. Everything outside this
 * module works in plain `[{ key, width }]` terms; the index<->key mapping
 * (via each grid's `defaultHeaders` declaration order) happens only here.
 * See docs/plans/grid-column-config-feature.md §6.
 */

import api from "@/services/axios_custom";
import API_ENDPOINTS from "@/util/api";

const PARAM_NAME = "ListViewConfig";

function getStaffId() {
  try {
    const userDetails = JSON.parse(localStorage.getItem("userDetails"));
    return userDetails?.staffId != null ? String(userDetails.staffId) : null;
  } catch {
    return null;
  }
}

// gridId convention: "<ModuleName>:<ListID>" (e.g. "Bookings:MiscellaneousNumber")
function getModuleName(gridId) {
  return gridId?.split(":")?.[0]?.trim() ?? "";
}

/**
 * "1:1:350|2:2:100" -> [{ key: "miscNumber", width: 350 }, { key: "number", width: 100 }]
 * `defaultHeaders` supplies the original declared order the indices refer to.
 * A width of 0 (legacy's "auto/no custom width" convention) maps to `undefined`.
 */
function deserializeLayout(paramValue, defaultHeaders) {
  if (!paramValue) return null;

  try {
    const entries = paramValue
      .split("|")
      .map((part) => {
        const [origIndexStr, positionStr, widthStr] = part.split(":");
        const origIndex = parseInt(origIndexStr, 10);
        const position = parseInt(positionStr, 10);
        const width = parseInt(widthStr, 10);
        const header = defaultHeaders[origIndex - 1];
        if (!header) return null;

        return {
          key: header.key,
          width: Number.isFinite(width) && width > 0 ? width : undefined,
          position: Number.isFinite(position) ? position : origIndex,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.position - b.position);

    if (!entries.length) return null;
    return entries.map(({ key, width }) => ({ key, width }));
  } catch {
    // Malformed value — treat as "no saved config" rather than breaking the grid.
    return null;
  }
}

/**
 * [{ key: "miscNumber", width: 350 }, { key: "number", width: 100 }] -> "1:1:350|2:2:100"
 */
function serializeLayout(columns, defaultHeaders) {
  return columns
    .map((col, i) => {
      const origIndex = defaultHeaders.findIndex((h) => h.key === col.key) + 1;
      if (origIndex <= 0) return null; // column no longer exists — drop it
      const position = i + 1;
      const width = col.width || 0;
      return `${origIndex}:${position}:${width}`;
    })
    .filter(Boolean)
    .join("|");
}

async function fetchModuleRows(gridId) {
  const moduleName = getModuleName(gridId);
  if (!moduleName) return [];

  const { data } = await api.get(
    API_ENDPOINTS.clientParams2.getByContextName +
      encodeURIComponent(moduleName.toLowerCase())
  );
  return Array.isArray(data) ? data : [];
}

function findExistingRow(rows, gridId, staffId) {
  const matches = rows.filter(
    (row) =>
      row?.paramName === PARAM_NAME &&
      row?.paramContext === gridId &&
      String(row?.staffID) === staffId
  );
  if (!matches.length) return null;

  // Duplicates shouldn't happen via this feature's own save flow, but the
  // live table already has some (see docs/testing/bio-client-params-test-cases.md
  // TC-5.4) — the most recently created row wins.
  return matches.reduce((latest, row) =>
    (row.clientParamID ?? 0) > (latest.clientParamID ?? 0) ? row : latest
  );
}

/**
 * @param {string} gridId - stable identifier for the grid, e.g. "Bookings:MiscellaneousNumber"
 * @param {Array<{key: string, text: string}>} defaultHeaders - the screen's default column declaration order
 * @returns {Promise<Array<{key: string, width?: number}>|null>} saved column layout, or null if none/invalid
 */
export async function getGridColumnConfig(gridId, defaultHeaders) {
  if (!gridId || !Array.isArray(defaultHeaders) || !defaultHeaders.length) {
    return null;
  }
  const staffId = getStaffId();
  if (!staffId) return null;

  try {
    const rows = await fetchModuleRows(gridId);
    const row = findExistingRow(rows, gridId, staffId);
    if (!row?.paramValue) return null;
    return deserializeLayout(row.paramValue, defaultHeaders);
  } catch (error) {
    console.error("getGridColumnConfig failed", error);
    return null;
  }
}

/**
 * @param {string} gridId
 * @param {Array<{key: string, width?: number}>} columns - order === column order
 * @param {Array<{key: string, text: string}>} defaultHeaders - the screen's default column declaration order
 * @returns {Promise<boolean>} whether the save succeeded
 */
export async function saveGridColumnConfig(gridId, columns, defaultHeaders) {
  if (!gridId || !Array.isArray(columns) || !Array.isArray(defaultHeaders)) {
    return false;
  }
  const staffId = getStaffId();
  if (!staffId) return false;

  try {
    // Look up any existing row for this user+grid so the save updates it
    // in place (clientParamID) rather than creating a duplicate row.
    const rows = await fetchModuleRows(gridId);
    const existing = findExistingRow(rows, gridId, staffId);

    await api.post(API_ENDPOINTS.clientParams2.save, {
      clientParamID: existing?.clientParamID ?? 0,
      paramName: PARAM_NAME,
      paramContext: gridId,
      paramValue: serializeLayout(columns, defaultHeaders),
      staffID: staffId,
      staffGroupsID: null,
    });

    return true;
  } catch (error) {
    console.error("saveGridColumnConfig failed", error);
    return false;
  }
}

/**
 * No clientparams2 delete endpoint exists yet — left as a documented no-op
 * (not currently called by useGridColumnConfig) until one is available.
 * @param {string} gridId
 * @returns {Promise<void>}
 */
export async function clearGridColumnConfig(gridId) {
  console.warn(
    `clearGridColumnConfig("${gridId}"): no clientparams2 delete endpoint available yet`
  );
}
